/**
 * Format time for display in input field
 * @param time Time string in HH:mm format
 * @param format '12h' or '24h' format
 * @param locale Locale string
 * @returns Formatted time string
 */
export const formatTimeForDisplay = (time: string, format: '12h' | '24h' = '24h', locale: string = 'en'): string => {
  if (!time) return '';
  
  const [hours, minutes] = time.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return '';
  
  if (format === '12h') {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
  }
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Parse time input string (HH:mm) to hours and minutes
 */
export const parseTimeInput = (value: string): { hours: number; minutes: number } | null => {
  if (!value) return null;
  
  const parts = value.split(':');
  if (parts.length !== 2) return null;
  
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  
  if (isNaN(hours) || isNaN(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  
  return { hours, minutes };
};

/**
 * Generate minute options array based on step
 * @param minuteStep Step size (1, 5, 10, 15, 30)
 * @returns Array of minute values
 */
export const generateTimeOptions = (minuteStep: number = 1): number[] => {
  const options: number[] = [];
  for (let i = 0; i < 60; i += minuteStep) {
    options.push(i);
  }
  return options;
};

/**
 * Validate time range
 */
export const validateTimeRange = (hours: number, minutes: number): boolean => {
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};

/**
 * Add hours to a time string
 * @param time Time string in HH:mm format
 * @param hoursToAdd Number of hours to add
 * @returns New time string in HH:mm format
 */
export const addHoursToTime = (time: string, hoursToAdd: number): string => {
  if (!time) return '';
  
  const parsed = parseTimeInput(time);
  if (!parsed) return '';
  
  const date = new Date();
  date.setHours(parsed.hours, parsed.minutes, 0, 0);
  const originalDay = date.getDate();
  date.setHours(date.getHours() + hoursToAdd);
  
  const hours = date.getHours();
  const minutes = date.getMinutes();
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Add hours to a time string and check if it crosses midnight
 * @param time Time string in HH:mm format
 * @param hoursToAdd Number of hours to add
 * @returns Object with new time and whether it crossed midnight
 */
export const addHoursToTimeWithDayCheck = (time: string, hoursToAdd: number): { time: string; crossedMidnight: boolean } => {
  if (!time) return { time: '', crossedMidnight: false };
  
  const parsed = parseTimeInput(time);
  if (!parsed) return { time: '', crossedMidnight: false };
  
  const date = new Date();
  date.setHours(parsed.hours, parsed.minutes, 0, 0);
  const originalDay = date.getDate();
  date.setHours(date.getHours() + hoursToAdd);
  
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const newDay = date.getDate();
  
  const crossedMidnight = newDay !== originalDay;
  
  return {
    time: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
    crossedMidnight
  };
};
